import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, catchError, concat, map, mergeMap, of, switchMap, timer } from 'rxjs';
import * as SchoolCalendarActions from './school-calendar.actions';
import { SchoolCalendarService } from '@core/services/school-calendar.service';

/**
 * How long the backend is given to project a write into what a read returns.
 *
 * <p>A write is a command: it is accepted before the list a read serves has been updated, so a
 * re-read taken the instant a save succeeds can still come back without the entry just saved.</p>
 */
export const PROJECTION_CATCH_UP_MS = 1500;

/**
 * Re-reads twice off one write: once now, so the page updates the moment the backend has already
 * caught up, and once after {@link PROJECTION_CATCH_UP_MS}, so it updates on its own when the
 * backend had not. Without the second read an admin has to reload the page to see what they added.
 */
function reloadNowAndAfterCatchUp<T extends Action>(action: T): Observable<T> {
  return concat(of(action), timer(PROJECTION_CATCH_UP_MS).pipe(map(() => action)));
}

/** Reduces the backend's FAILED status code, an HttpErrorResponse or an Error to one message. */
export function extractSchoolCalendarErrorMessage(error: any): string {
  if (typeof error === 'string') {
    return error;
  }
  if (error?.status === 'FAILED' && error?.reason) {
    return error.reason;
  }
  if (error?.error?.statusCode?.reason) {
    return error.error.statusCode.reason;
  }
  if (error?.message) {
    return error.message;
  }
  return 'An unexpected error occurred. Please try again.';
}

@Injectable()
export class SchoolCalendarEffects {
  constructor(private actions$: Actions, private schoolCalendarService: SchoolCalendarService) {}

  loadExceptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchoolCalendarActions.loadExceptions),
      switchMap(({ from, to }) =>
        this.schoolCalendarService.loadExceptions(from, to).pipe(
          map((exceptions) => SchoolCalendarActions.loadExceptionsSuccess({ exceptions })),
          catchError((error) =>
            of(SchoolCalendarActions.loadExceptionsFailure({ error: extractSchoolCalendarErrorMessage(error) }))
          )
        )
      )
    )
  );

  saveException$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchoolCalendarActions.saveException),
      mergeMap(({ request }) =>
        this.schoolCalendarService.saveException(request).pipe(
          map(() => SchoolCalendarActions.saveExceptionSuccess()),
          catchError((error) =>
            of(SchoolCalendarActions.saveExceptionFailure({
              request,
              error: extractSchoolCalendarErrorMessage(error)
            }))
          )
        )
      )
    )
  );

  cancelException$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchoolCalendarActions.cancelException),
      mergeMap(({ exceptionId }) =>
        this.schoolCalendarService.cancelException(exceptionId).pipe(
          map(() => SchoolCalendarActions.cancelExceptionSuccess({ exceptionId })),
          catchError((error) =>
            of(SchoolCalendarActions.cancelExceptionFailure({
              exceptionId,
              error: extractSchoolCalendarErrorMessage(error)
            }))
          )
        )
      )
    )
  );

  /**
   * A saved exception changes what the calendar shows, so the list is re-read from the backend
   * rather than patched locally: the backend derives each entry's status from its dates, and an
   * emergency closure is stamped with the server's clock, so only a reload is trustworthy.
   */
  reloadAfterSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchoolCalendarActions.saveExceptionSuccess),
      switchMap(() => reloadNowAndAfterCatchUp(SchoolCalendarActions.loadExceptions({})))
    )
  );

  loadSchedules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchoolCalendarActions.loadSchedules),
      switchMap(() =>
        this.schoolCalendarService.loadAttendanceSchedules().pipe(
          map((schedules) => SchoolCalendarActions.loadSchedulesSuccess({ schedules })),
          catchError((error) =>
            of(SchoolCalendarActions.loadSchedulesFailure({ error: extractSchoolCalendarErrorMessage(error) }))
          )
        )
      )
    )
  );

  saveSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchoolCalendarActions.saveSchedule),
      mergeMap(({ request }) =>
        this.schoolCalendarService.setAttendanceSchedule(request).pipe(
          map(() => SchoolCalendarActions.saveScheduleSuccess()),
          catchError((error) =>
            of(SchoolCalendarActions.saveScheduleFailure({ error: extractSchoolCalendarErrorMessage(error) }))
          )
        )
      )
    )
  );

  removeSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchoolCalendarActions.removeSchedule),
      mergeMap(({ pupilAccountId }) =>
        this.schoolCalendarService.removeAttendanceSchedule(pupilAccountId).pipe(
          map(() => SchoolCalendarActions.removeScheduleSuccess()),
          catchError((error) =>
            of(SchoolCalendarActions.removeScheduleFailure({ error: extractSchoolCalendarErrorMessage(error) }))
          )
        )
      )
    )
  );

  reloadSchedulesAfterWrite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchoolCalendarActions.saveScheduleSuccess, SchoolCalendarActions.removeScheduleSuccess),
      switchMap(() => reloadNowAndAfterCatchUp(SchoolCalendarActions.loadSchedules()))
    )
  );
}
